import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Seo } from "@/components/Seo";
import { Marker, Peace } from "@/components/brand/Doodles";
import { MascotDoodle } from "@/components/brand/MascotDoodle";
import { HangTags } from "@/components/brand/HangTags";
import { Reveal } from "@/components/Reveal";
import { breadcrumb } from "@/lib/site";

const VALUES = [["Curiosity", "We question everything, especially what everyone agrees on."], ["Creativity", "Original art, hand-drawn graphics, zero copy-paste."], ["Community", "Toronto made us. We collab with local artists and give back."], ["Good People", "Kindness first. Sus ideas second. Always both."]];
const BELIEFS = ["Question the usual", "Support original art", "People over trends", "Kindness is always in style", "Stay a little sus"];
const FAQ = [
  ["How much is shipping?", "Shipping is free across Canada on orders over C$100. Under that, it's a flat C$10. Orders ship from Toronto in 1 to 3 business days."],
  ["What is your return policy?", "You have 30 days to return unworn items with tags attached for a refund or exchange. Exchanges within Canada are free."],
  ["How does your sizing work?", "All apparel is unisex, XS to XXL, with an oversized boxy fit. Size down for a regular fit. Caps, beanies and totes are one size."],
  ["Where do you ship?", "We ship everywhere in Canada and to the USA. More countries are coming with Drop 002, Sus World."],
];

const faqLd = { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) };

export default function About() {
  return (
    <div className="mx-auto max-w-[1440px] px-5 pb-28 sm:px-10" data-testid="about-page">
      <Seo title="About: Not your average crew" description="the suspects is a Gen Z streetwear brand from Toronto, est. 2024. Our values: curiosity, creativity, community and good people. FAQ on shipping, returns and sizing." path="/about" image="/img/about_wide.webp" jsonLd={[faqLd, breadcrumb([{ name: "Home", path: "/" }, { name: "About", path: "/about" }])]} />
      <header className="grid items-end gap-10 pb-16 pt-16 lg:grid-cols-12 lg:pt-24">
        <h1 className="display text-[clamp(4rem,9.5vw,8.75rem)] lg:col-span-7">Not your<br />average crew.</h1>
        <div className="space-y-5 text-[15px] leading-relaxed text-ink/75 lg:col-span-5 lg:pl-10">
          <p className="label flex items-center gap-3 text-ink"><Peace size={26} /> Toronto, CA | Est. 2024</p>
          <p>the suspects started in 2024 in Toronto with a sketchbook, a screen-printing kit and one nagging question: why does everything look the same?</p>
          <p>We make clothing for the curious kind. People who side-eye the algorithm, collect weird ideas and keep good people close. Small drops, original art, made to be worn into the ground.</p>
        </div>
      </header>

      <section className="grid gap-px lg:grid-cols-12" aria-labelledby="values-title">
        <Reveal className="overflow-hidden rounded-[24px] bg-cloud lg:col-span-8">
          <img src="/img/about_wide.webp" alt="A crew of friends walking under a concrete overpass in Toronto" loading="lazy" className="photo aspect-[16/10] h-full w-full object-cover" />
        </Reveal>
        <div className="bg-cloud py-10 lg:col-span-4 lg:py-0 lg:pl-12">
          <h2 id="values-title" className="label text-ink/50 lg:pt-2">Our values</h2>
          <ul className="mt-6">
            {VALUES.map(([t, d], i) => (
              <li key={t} className="border-b border-ink/10 py-5 last:border-b-0" data-testid={`value-${t.toLowerCase().replace(/\s+/g, "-")}`}>
                <p className="flex gap-4 font-mono text-sm font-bold uppercase tracking-[0.2em]"><span className="text-ink/40">0{i + 1}</span>{t}</p>
                <p className="mt-2 pl-9 text-[13px] text-ink/60">{d}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="grid items-center gap-10 py-24 md:grid-cols-12 lg:py-32" aria-label="The crew">
        <Reveal className="md:col-span-4">
          <figure className="-rotate-2 rounded-[24px] bg-white p-3 pb-14 shadow-[0_10px_30px_rgba(17,17,17,0.08)]">
            <img src="/img/look_street_3.webp" alt="Two friends laughing on concrete steps in downtown Toronto" loading="lazy" className="photo aspect-square w-full rounded-[16px] object-cover" />
            <figcaption className="mt-4 text-center"><Marker className="text-xl" rotate={0}>Toronto, 2024</Marker></figcaption>
          </figure>
        </Reveal>
        <Reveal delay={120} className="overflow-hidden rounded-[24px] md:col-span-8">
          <img src="/img/hero_crew.webp" alt="Three members of the crew in the suspects hoodies against a concrete wall" loading="lazy" className="photo aspect-[16/10] w-full object-cover" />
        </Reveal>
      </section>

      <section className="grid items-center gap-12 border-t border-ink/10 pt-20 lg:grid-cols-12" aria-labelledby="tags-title">
        <div className="lg:col-span-5">
          <p className="label text-ink/50">Hang tags</p>
          <h2 id="tags-title" className="display mt-5 text-[clamp(3rem,6vw,5.5rem)]">Certified sus.<br />Every piece.</h2>
          <p className="mt-6 max-w-sm text-[15px] leading-relaxed text-ink/70">Every garment ships with two tags: the black one says who made it, the cream one asks a question. Keep them. Frame them. Question them.</p>
        </div>
        <div className="flex items-center justify-center gap-10 lg:col-span-7">
          <HangTags />
          <MascotDoodle variant="crown" size={150} className="hidden xl:block" />
        </div>
      </section>

      <section className="grid gap-10 border-t border-ink/10 pt-20 mt-24 lg:grid-cols-12" aria-labelledby="beliefs-title">
        <h2 id="beliefs-title" className="display text-[clamp(3rem,6vw,5.5rem)] lg:col-span-5">Our beliefs</h2>
        <ol className="lg:col-span-7" data-testid="beliefs-list">
          {BELIEFS.map((b, i) => (
            <li key={b} className="flex items-baseline gap-8 border-b border-ink/10 py-5 font-mono text-sm uppercase tracking-[0.2em] sm:text-base">
              <span className="text-ink/40">{String(i + 1).padStart(2, "0")}</span>
              <span className="font-bold">{b}</span>
            </li>
          ))}
        </ol>
      </section>

      <section id="faq" className="grid scroll-mt-36 gap-10 pt-28 lg:grid-cols-12" aria-labelledby="faq-title">
        <div className="lg:col-span-5">
          <h2 id="faq-title" className="display text-[clamp(3rem,6vw,5.5rem)]">FAQ</h2>
          <p className="mt-4 text-[15px] text-ink/60">Still curious? hello@thesuspects.ca</p>
        </div>
        <Accordion type="single" collapsible className="border-t border-ink/10 lg:col-span-7" data-testid="faq-accordion">
          {FAQ.map(([q, a], i) => (
            <AccordionItem key={q} value={`q${i}`} className="border-b border-ink/10">
              <AccordionTrigger className="py-6 text-left text-base font-medium hover:no-underline" data-testid={`faq-trigger-${i}`}>{q}</AccordionTrigger>
              <AccordionContent className="pb-6 text-[15px] leading-relaxed text-ink/70">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
}
